import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import CableIcon from '@mui/icons-material/Cable';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { RequirePermission } from '../security';
import { InventoryType, UserPermission, UserRoleCategoryPath } from '../enums/';
import {
  Container,
  DecoratedHeader,
  ImpersonateDrawer,
  InventoryFilters,
  InventoryLastSyncDate,
  InventorySpecifications,
  InventoryTable,
  InventoryWarningMessageCard,
  MultipleReservationsDrawer,
  ShoppingTruck,
  Show,
} from '../components';
import {
  ImpersonatedCustomer,
  InventoryFilters as InventoryFiltersType,
  Nullable,
} from '../types';
import {
  useAuthentication,
  useCompany,
  useCurrentUser,
  useInventorySearchLogs,
  useLocalStorage,
  useNotification,
  usePageTitle,
  usePortalSettings,
  useSorting,
} from '../hooks';
import { getPageTitle } from '../utils/pages';
import { Company, Reel, User } from '../models';
import { ReelWithInventory } from '../models/Reel';
import { isSelectedFunction } from '../components/InventoryTable/utils';
import { usePrompt } from '../navigation/ReactRouterHooks';
import { AgentImpersonateDrawer } from '../components/AgentImpersonateDrawer/AgentImpersonateDrawer.component';
import { isUser } from '../utils/user';
import { InventoryFiltersConverter } from '../hooks/useInventorySearchLogs';
import { emptyFilters } from '../components/InventoryFilters/InventoryFilters.interface';

const SEARCH_DELAY_TIMER = 60 * 1000; // 1 minute

const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isAgent, isCorporateUser, userRoleCategory } =
    useAuthentication();
  const location = useLocation();
  const navigate = useNavigate();
  const { clearNotification } = useNotification();
  const user = useCurrentUser();
  const [inventoryType, setInventoryType] = useState(InventoryType.CSA);
  const { item: portalSettings, loading: loadingPortalSettings } =
    usePortalSettings();
  const { item: filters, setItem: setFilters } =
    useLocalStorage<InventoryFiltersType>(
      'inventory',
      inventoryType === InventoryType.CSA ? 'csa' : 'ul'
    );
  const {
    item: searchLog,
    setItem: setSearchLog,
    removeItem: deleteSearchLog,
  } = useLocalStorage<string>('searchLog', 'id');

  const {
    item: searchTimestamp,
    setItem: setSearchTimestamp,
    removeItem: deleteSearchTimestamp,
  } = useLocalStorage<string>('searchLog', 'timestamp');
  const sorting = useSorting('description');
  const [searchParams, setSearchParams] = useSearchParams();
  const isMounted = useRef(false);

  const { createLog, logIsValid } = useInventorySearchLogs();

  const [show, setShow] = useState<boolean>(false);
  const [multipleReservations, setMultipleReservations] = useState(false);
  const [shoppingTruck, setShoppingTruck] = useState(false);
  const [selectedReels, setSelectedReels] = useState<ReelWithInventory[]>([]);
  const [openedCollapse, setOpenedCollapse] = useState<Nullable<string>>(null);
  const [units, setUnits] = useState(user.measurementUnits);
  const [impersonatedUser, setImpersonatedUser] =
    useState<Nullable<User>>(null);
  const [impersonatedCustomer, setImpersonatedCustomer] =
    useState<Nullable<ImpersonatedCustomer>>(null);
  const [currentCompany, setCurrentCompany] = useState<Nullable<Company>>(null);

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const pathPrefix = UserRoleCategoryPath[userRoleCategory];
  const handleDoneClick = () => {
    setImpersonatedUser(null);
    setImpersonatedCustomer(null);
    clearNotification();
  };

  const storedFilterString = useMemo(
    () => (filters ? InventoryFiltersConverter.toFirestore(filters) : null),
    [filters]
  );

  const handleSearch = async (filters: Nullable<InventoryFiltersType>) => {
    const oldFilters = storedFilterString;
    setFilters({
      ...filters,
      standardType: inventoryType,
    } as InventoryFiltersType);

    if (
      !filters ||
      !(
        filters?.productType ||
        filters?.partNumber ||
        filters?.customPartNumber
      )
    ) {
      deleteSearchLog();
      deleteSearchTimestamp();
      setShow(false);
    } else {
      setShow(true);
      const now = Date.now();
      const newFilters = InventoryFiltersConverter.toFirestore(filters);
      if (
        (oldFilters !== newFilters ||
          !searchTimestamp ||
          now - parseInt(searchTimestamp) > SEARCH_DELAY_TIMER) &&
        logIsValid(filters)
      ) {
        try {
          const { id: logId } = await createLog(filters);

          setSearchLog(logId);
          setSearchTimestamp(now.toString());
        } catch {
          deleteSearchLog();
          deleteSearchTimestamp();
        }
      }
    }
  };

  usePrompt(
    t('forms.navigationConfirmation'),
    (multipleReservations && selectedReels.length > 0) || false
  );

  const pageTitleOptions = {
    type: t(`inventoryType.${inventoryType}`),
  };
  usePageTitle(getPageTitle('inventory', pageTitleOptions));

  useEffect(() => {
    const partNumber = searchParams.get('part');
    if (partNumber && !isMounted.current) {
      handleSearch({
        ...(filters as InventoryFiltersType),
        partNumber,
        searchByCable: false,
      });
      setSearchParams(new URLSearchParams());
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (user && !user.canSeeCSA) {
      setInventoryType(InventoryType.UL);
    }

    if (user && !user.canSeeUL) {
      setInventoryType(InventoryType.CSA);
    }

    if (user && !user?.canSeeCSA && !user?.canSeeUL) {
      navigate('/');
    }

    if (!location.pathname.startsWith(pathPrefix)) {
      navigate(`${pathPrefix}/inventory}`);
    }
  }, [user, inventoryType, navigate, pathPrefix, location.pathname]);

  useEffect(() => {
    if (!isDesktop) {
      setMultipleReservations(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  useEffect(() => {
    if (selectedReels.length > 0) {
      resetSelectedReels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const companyId = impersonatedCustomer
    ? impersonatedCustomer.company.companyId
    : user.companyRef.id;
  const { item: company } = useCompany(companyId);

  useEffect(() => {
    if (!companyId) {
      setCurrentCompany(null);
    } else {
      setCurrentCompany(company);
    }
  }, [company, companyId, impersonatedCustomer]);

  const showSpecifications = show && !!filters?.productType;
  const showTable = show && !!filters && !!inventoryType && !!user;

  const displayMultipleReservationsDrawer = useMemo(() => {
    if (isAdmin && !impersonatedUser && !impersonatedCustomer) return false;
    if (isAgent && !impersonatedCustomer) return false;
    if (isCorporateUser && !impersonatedCustomer) return false;
    return multipleReservations && isDesktop;
  }, [
    impersonatedCustomer,
    impersonatedUser,
    isAdmin,
    isAgent,
    isCorporateUser,
    isDesktop,
    multipleReservations,
  ]);

  const toggleShoppingTruck = () => {
    setShoppingTruck(!shoppingTruck);
  };

  const toggleItemSelection = (item: Reel) => {
    const itemToToggle: ReelWithInventory = {
      ...item,
      inventoryType,
    };

    if (!isSelectedFunction(selectedReels, itemToToggle)) {
      setSelectedReels((prev) => [...prev, itemToToggle]);
    } else {
      setSelectedReels((prev) =>
        prev.filter((filterItem) => itemToToggle.id !== filterItem.id)
      );
    }
  };

  const resetSelectedReels = () => {
    if (selectedReels.length) {
      setSelectedReels([]);
    }
  };

  const resetMultipleReservations = () => {
    resetSelectedReels();
    setMultipleReservations(false);
  };

  const resetShoppingTruck = (failedReservations: string[]) => {
    setSelectedReels((prev) =>
      prev.filter((filterItem) => failedReservations.includes(filterItem.id))
    );
    setShoppingTruck(false);
  };

  const onAdminImpersonate = (user: User | ImpersonatedCustomer) => {
    resetSelectedReels();
    if (isUser(user)) {
      setImpersonatedUser(user);
    } else {
      setImpersonatedCustomer(user);
    }
    navigate('/admin/inventory');
  };

  const onAgentImpersonate = (customer: ImpersonatedCustomer) => {
    resetSelectedReels();
    setImpersonatedCustomer(customer);
    navigate('/agent/inventory');
  };

  const onCorporateImpersonate = (customer: ImpersonatedCustomer) => {
    resetSelectedReels();
    setImpersonatedCustomer(customer);
    navigate('/corporate-user/inventory');
  };

  const onCancel = () => {
    resetMultipleReservations();
    navigate(location.pathname.replace('/reserve', ''));
  };

  const onLoadProducts = (type: InventoryType) => {
    setInventoryType(type);
  };

  return (
    <RequirePermission oneOf={[UserPermission.ViewInventory]}>
      <Container>
        <DecoratedHeader
          icon={<CableIcon />}
          title={t('navigation.inventory')}
          variant="colored"
        >
          <RadioGroup
            row
            name="inventoryType"
            value={inventoryType === InventoryType.CSA ? 'csa' : 'ul'}
            onChange={(_, value) => {
              setInventoryType(
                value === 'csa' ? InventoryType.CSA : InventoryType.UL
              );
              handleSearch({
                ...emptyFilters,
                standardType:
                  value === 'csa' ? InventoryType.CSA : InventoryType.UL,
                searchByCable: filters?.searchByCable,
              } as InventoryFiltersType);
            }}
          >
            {user?.canSeeCSA && (
              <FormControlLabel
                value="csa"
                control={<Radio />}
                label="CSA"
                disabled={
                  (!!impersonatedUser && !impersonatedUser.canSeeCSA) ||
                  (!!impersonatedCustomer && !impersonatedCustomer.canSeeCSA)
                }
              />
            )}
            {user?.canSeeUL && (
              <FormControlLabel
                value="ul"
                control={<Radio />}
                label="UL"
                disabled={
                  (!!impersonatedUser && !impersonatedUser.canSeeUL) ||
                  (!!impersonatedCustomer && !impersonatedCustomer.canSeeUL)
                }
              />
            )}
          </RadioGroup>
        </DecoratedHeader>

        {/* Temporary notice about delayed shipments begin */}
        <Show
          if={
            !loadingPortalSettings &&
            !!portalSettings?.inventoryWarningMessageShow
          }
        >
          {portalSettings && (
            <InventoryWarningMessageCard
              header={portalSettings?.inventoryWarningMessageHeading}
              body={portalSettings?.inventoryWarningMessageBody}
              color={portalSettings?.inventoryWarningMessageColor}
            />
          )}
        </Show>
        {/* Temporary notice about delayed shipments end */}
      </Container>

      <InventoryFilters
        onSearch={handleSearch}
        filters={filters ?? undefined}
        showCustomPartNumber={currentCompany?.hasCustomPartNumbers ?? false}
        inventoryType={inventoryType === InventoryType.CSA ? 'csa' : 'ul'}
      />

      <Show if={showSpecifications}>
        <InventorySpecifications filters={filters as InventoryFiltersType} />
      </Show>

      <Container
        sx={{ paddingBottom: showTable && multipleReservations ? '4.5em' : '' }}
      >
        <Show if={showTable}>
          <InventoryTable
            openedCollapse={openedCollapse}
            setOpenedCollapse={setOpenedCollapse}
            type={inventoryType}
            filters={filters}
            setFilters={setFilters}
            sorting={sorting}
            user={user}
            impersonatedCustomer={impersonatedCustomer}
            impersonatedUser={impersonatedUser}
            measureUnits={units}
            setMeasureUnits={setUnits}
            multipleReservations={multipleReservations}
            setMultipleReservations={setMultipleReservations}
            resetMultipleReservations={resetMultipleReservations}
            selectedReels={selectedReels as Reel[]}
            toggleItemSelection={toggleItemSelection}
            isDesktop={isDesktop}
            inventorySearchLogId={searchLog || ''}
            onLoadProducts={onLoadProducts}
          />

          <InventoryLastSyncDate timeZone={user?.timeZone} />
        </Show>
      </Container>

      <Show if={displayMultipleReservationsDrawer}>
        <MultipleReservationsDrawer
          impersonatedUser={impersonatedUser}
          impersonatedCustomer={impersonatedCustomer}
          selectedReels={selectedReels}
          shoppingTruck={shoppingTruck}
          toggleShoppingTruck={toggleShoppingTruck}
          user={user}
        />

        <Show if={shoppingTruck}>
          <ShoppingTruck
            impersonatedUser={impersonatedUser}
            impersonatedCustomer={impersonatedCustomer}
            measureUnits={units}
            resetShoppingTruck={resetShoppingTruck}
            selectedReels={selectedReels}
            toggleItemSelection={toggleItemSelection}
            toggleShoppingTruck={toggleShoppingTruck}
            user={user}
          />
        </Show>
      </Show>
      <Show
        if={
          isAdmin &&
          (!!impersonatedCustomer || !!impersonatedUser) &&
          !multipleReservations
        }
      >
        <ImpersonateDrawer
          impersonatedUser={impersonatedUser}
          impersonatedCustomer={impersonatedCustomer}
          onDoneClicked={handleDoneClick}
        />
      </Show>
      <Show
        if={
          (isAgent || isCorporateUser) &&
          !!impersonatedCustomer &&
          !multipleReservations
        }
      >
        <AgentImpersonateDrawer
          impersonatedCustomer={impersonatedCustomer}
          onDoneClicked={handleDoneClick}
        />
      </Show>

      <Outlet
        context={{
          fromCollapse: !!openedCollapse,
          inventoryType,
          onCancel,
          onCorporateImpersonate,
          onAgentImpersonate,
          onAdminImpersonate,
          title: 'impersonateCustomer.reserve',
        }}
      />
    </RequirePermission>
  );
};

export default Inventory;
